/**
 * @file EmptyView.ts
 * @description
 * -----------------------------------------------------
 * Archivo que contiene el componente de sección vacía
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-05-29
 * @author DLTCode
 * @author Carlos Domenech
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { useTranslations } from "next-intl";
import ImageComponent from "@/components/ui/ImageComponent";
import { HTMLProps } from "react";
import clsx from "clsx";

interface EmptyViewProps extends Omit<HTMLProps<HTMLDivElement>, "size"> {
  /** Mensaje personalizado que sobreescribe el mensaje por defecto */
  message?: string;
  /**
   * Tamaño de la vista de error (afecta las dimensiones de la imagen)
   * @default "large"
   * */
  size?: "small" | "medium" | "large";
  /** Contenido adicional renderizado debajo del texto */
  children?: React.ReactNode;
}

export default function EmptyView({
  size = "large",
  message,
  className,
  children,
  ...props
}: EmptyViewProps) {
  const status = useTranslations("Status");

  return (
    <section
      className={clsx(`empty error-view error-view__${size}`, className)}
      {...props}
    >
      <section className="error-view__image">
        {/* Ilustración decorativa: refuerza visualmente el mensaje contiguo.
            Usamos alt vacío para no duplicar contenido en lectores de pantalla. */}
        <ImageComponent
          src={"/assets/img/empty.png"}
          alt=""
          width={300}
          height={133}
          zoomable={false}
        />
      </section>
      <p className={`error-view__title`}>{status("emptySection")}</p>
      <p>{message}</p>
      <section className="empty__actions">{children}</section>
    </section>
  );
}
